<script setup lang="ts">
import {
  TASK_DESCRIPTION_MAX,
  addDays,
  moveInList,
  taskNotifyMessage,
  type Action,
  type TaskProposal,
} from '@cockpit/shared';
import { computed, onMounted, ref } from 'vue';
import { ApiError } from '../api';
import { assistantApi } from '../api/assistant';
import { useMembers } from '../composables/useMembers';
import { useAuthStore } from '../stores/auth';
import { useToastStore } from '../stores/toast';
import { useOfflineStore } from '../stores/offline';
import { createdToast, sendNotice, useNotifyDraft, useTaskNotifyOptions } from '../taskNotify';
import ChecklistEditor from './afaire/ChecklistEditor.vue';
import TaskNotifyFields from './afaire/TaskNotifyFields.vue';

/**
 * Tâche proposée par l'assistant (ou une piste du Conseiller) : le formulaire de tâche manuelle s'ouvre
 * prérempli ; rien n'est créé tant que la personne n'a pas cliqué « Ajouter » (POST /api/actions). Lot AS : la
 * description et les étapes proposées se relisent et se modifient avant l'ajout. Lot AZ : « Prévenir <Prénom> » comme
 * dans « À faire » quand la tâche est pour quelqu'un d'autre.
 */
const props = defineProps<{
  proposal: Pick<
    TaskProposal,
    'title' | 'assigneeId' | 'dueDate' | 'clientId' | 'projectId' | 'description' | 'steps'
  >;
}>();
const emit = defineEmits<{ created: [action: Action]; cancel: [] }>();
const auth = useAuthStore();
const toast = useToastStore();
const { members, load } = useMembers();

const today = auth.config?.today ?? new Date().toISOString().slice(0, 10);
const title = ref(props.proposal.title);
const assigneeId = ref(props.proposal.assigneeId ?? auth.me?.id ?? '');
const dueDate = ref(props.proposal.dueDate ?? addDays(today, 3));
const description = ref(props.proposal.description ?? '');
let stepSeq = 0;
const nextId = () => `draft-${(stepSeq += 1)}`;
const steps = ref((props.proposal.steps ?? []).map((label) => ({ id: nextId(), label, done: false })));
const busy = ref(false);

/** Lot AZ « Le prévenir » : seulement pour quelqu'un d'autre ; rien ne part sans la case. */
const offline = useOfflineStore();
const { options: notifyOptions, refresh: refreshNotifyOptions } = useTaskNotifyOptions();
const forOther = computed(() => !!assigneeId.value && assigneeId.value !== auth.me?.id);
const whoName = computed(() => members.value?.find((m) => m.id === assigneeId.value)?.name ?? '');
const {
  on: notifyOn,
  channel: notifyChannel,
  message: notifyMessage,
  setMessage: setNotifyMessage,
} = useNotifyDraft(() =>
  taskNotifyMessage({ kind: 'created', title: title.value || '…', dueDate: dueDate.value || today }),
);

onMounted(() => {
  void load();
  void refreshNotifyOptions();
});

async function submit(): Promise<void> {
  if (!title.value.trim() || !assigneeId.value || !dueDate.value) return;
  busy.value = true;
  const desc = description.value.trim();
  const notice =
    forOther.value && notifyOn.value && offline.online && notifyMessage.value.trim() !== ''
      ? { channel: notifyChannel.value, message: notifyMessage.value }
      : null;
  try {
    const action = await assistantApi.createTask({
      title: title.value.trim(),
      assigneeId: assigneeId.value,
      dueDate: dueDate.value,
      ...(props.proposal.clientId ? { clientId: props.proposal.clientId } : {}),
      ...(props.proposal.projectId ? { projectId: props.proposal.projectId } : {}),
      ...(desc ? { description: desc } : {}),
      ...(steps.value.length > 0 ? { steps: steps.value.map((s) => s.label) } : {}),
    });
    const who = members.value?.find((m) => m.id === action.assigneeId)?.name ?? 'toi';
    const base = `Tâche ajoutée à « À faire » pour ${who}`;
    const outcome = notice ? await sendNotice(action.id, notice.channel, notice.message) : null;
    toast.show(
      outcome ? createdToast(base, who, outcome) : `${base}.`,
      outcome?.ok === false ? 8000 : undefined,
    );
    emit('created', action);
  } catch (err) {
    toast.show(err instanceof ApiError ? err.message : 'Ajout impossible.');
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <form class="task-form" data-testid="assistant-task-form" @submit.prevent="submit">
    <label class="field">Tâche<input v-model="title" type="text" maxlength="300" required /></label>
    <div class="row">
      <label class="field"
        >Pour<select v-model="assigneeId" required>
          <option v-for="m in members ?? []" :key="m.id" :value="m.id">{{ m.name }}</option>
        </select></label
      >
      <label class="field">Pour le<input v-model="dueDate" type="date" required /></label>
    </div>
    <TaskNotifyFields
      v-if="forOther"
      v-model:on="notifyOn"
      v-model:channel="notifyChannel"
      :message="notifyMessage"
      :name="whoName"
      :options="notifyOptions"
      :online="offline.online"
      :disabled="busy"
      @update:message="setNotifyMessage"
    />
    <label class="field"
      >Description<textarea
        v-model="description"
        rows="3"
        :maxlength="TASK_DESCRIPTION_MAX"
        placeholder="Facultatif"
        data-testid="assistant-task-description"
      ></textarea>
    </label>
    <div class="field">
      Étapes
      <ChecklistEditor
        :items="steps"
        :checkable="false"
        @add="(label) => (steps = [...steps, { id: nextId(), label, done: false }])"
        @rename="(id, label) => (steps = steps.map((s) => (s.id === id ? { ...s, label } : s)))"
        @remove="(id) => (steps = steps.filter((s) => s.id !== id))"
        @move="(id, to) => (steps = moveInList(steps, id, to))"
      />
    </div>
    <div class="row">
      <button class="btn primary sm" type="submit" :disabled="busy">Ajouter à « À faire »</button>
      <button class="btn sm" type="button" @click="emit('cancel')">Annuler</button>
    </div>
  </form>
</template>
